// AI interior design for one wall (functions/api/design/wall.ts).
//
// The editor's WallDesigner overlay lets a builder drag a box over a wall.
// From that box we:
//   - crop the rendered view (the canvas keeps its drawing buffer) and take a
//     small shot of the whole view for context;
//   - raycast through the box to find what wall it is: a GathrXR primitive
//     (box/plane → its textureUrl) or one material of an imported model
//     (→ that slot's materialTextures entry);
//   - ask the server for three designs and apply the one the builder picks.
import * as THREE from 'three';
import { useWorldStore } from '../stores/worldStore';
import { broadcastObjectUpdate } from './worldBroadcast';
import { gathrxrAuthHeader } from '../utils/quotaHeaders';

// ---- The scene, shared from inside the <Canvas> (WallDesignBridge) ----
interface ThreeRefs { gl: THREE.WebGLRenderer; scene: THREE.Scene; camera: THREE.Camera }
let three: ThreeRefs | null = null;
export const setWallDesignThree = (t: ThreeRefs | null) => { three = t; };
/** For tests/debugging: the scene the designer is attached to. */
export const getWallDesignThree = () => three;

export interface ScreenRect { x: number; y: number; w: number; h: number } // CSS px, relative to the canvas

export type WallTarget =
  | { kind: 'primitive'; id: string; label: string; aspect: number }
  | { kind: 'model'; id: string; slot: string; label: string; aspect: number };

export interface DesignOption { title: string; kind: 'mural' | 'pattern'; url: string }

export function canvasEl(): HTMLCanvasElement | null { return three?.gl.domElement ?? null; }

/** JPEG data URL of part of the canvas (CSS px rect), longest side ≤ max. */
function grab(rect: ScreenRect | null, max: number): string {
  const c = three!.gl.domElement;
  const sx = c.width / c.clientWidth, sy = c.height / c.clientHeight;
  const r = rect ? { x: rect.x * sx, y: rect.y * sy, w: rect.w * sx, h: rect.h * sy } : { x: 0, y: 0, w: c.width, h: c.height };
  const k = Math.min(1, max / Math.max(r.w, r.h));
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(r.w * k)); out.height = Math.max(1, Math.round(r.h * k));
  // Render now so the buffer holds the current frame (no selection outlines lag).
  three!.gl.render(three!.scene, three!.camera);
  out.getContext('2d')!.drawImage(c, r.x, r.y, r.w, r.h, 0, 0, out.width, out.height);
  return out.toDataURL('image/jpeg', 0.88);
}

const _rc = new THREE.Raycaster();
const _v = new THREE.Vector2();

/** What is under the box: the object most of the sample rays hit first. */
export function pickWallTarget(rect: ScreenRect): WallTarget | null {
  if (!three) return null;
  const { gl, scene, camera } = three;
  const cw = gl.domElement.clientWidth, ch = gl.domElement.clientHeight;
  const st = useWorldStore.getState();
  const votes = new Map<string, { n: number; t: WallTarget }>();
  const samples = [[0.5, 0.5], [0.3, 0.3], [0.7, 0.3], [0.3, 0.7], [0.7, 0.7]];
  for (const [fx, fy] of samples) {
    _v.set(((rect.x + rect.w * fx) / cw) * 2 - 1, -((rect.y + rect.h * fy) / ch) * 2 + 1);
    _rc.setFromCamera(_v, camera);
    for (const h of _rc.intersectObjects(scene.children, true)) {
      const o = h.object as THREE.Mesh;
      if (!(o as any).isMesh || !o.visible) continue;
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      if (mats.every((m) => !m || (m as THREE.Material).visible === false)) continue;
      // Walk up to the object the editor knows about.
      let id: string | null = null;
      for (let p: THREE.Object3D | null = o; p && !id; p = p.parent) {
        const oid = p.userData?.objectId as string | undefined;
        if (oid && (st.primitives[oid] || st.contentItems[oid])) id = oid;
        else if (p.name?.startsWith('primitive-') && st.primitives[p.name.slice(10)]) id = p.name.slice(10);
      }
      if (!id) break; // first solid hit isn't something we can retexture
      let t: WallTarget | null = null;
      const prim = st.primitives[id];
      if (prim) {
        if (!['box', 'plane', 'cylinder'].includes(prim.shape)) break;
        // Which face: its normal in the primitive's own frame picks the face size.
        const n = h.face ? h.face.normal.clone() : new THREE.Vector3(0, 0, 1);
        const [sx, sy, sz] = prim.scale;
        const ax = Math.abs(n.x), ay = Math.abs(n.y), az = Math.abs(n.z);
        const dims = prim.shape === 'plane' ? [sx, sy] : az >= ax && az >= ay ? [sx, sy] : ax >= ay ? [sz, sy] : [sx, sz];
        t = { kind: 'primitive', id, label: prim.name || 'Wall', aspect: Math.max(0.15, dims[0] / Math.max(0.01, dims[1])) };
      } else {
        const mi = h.face && Array.isArray(o.material) ? h.face.materialIndex : 0;
        const m = (Array.isArray(o.material) ? o.material[mi] : o.material) as THREE.Material;
        const slot = m?.name;
        if (!slot) break;
        const item = st.contentItems[id];
        t = { kind: 'model', id, slot, label: `${item?.filename || 'Model'} · ${slot}`, aspect: rect.w / Math.max(1, rect.h) };
      }
      const key = t.kind === 'model' ? `${t.id}|${t.slot}` : t.id;
      const v = votes.get(key) || { n: 0, t };
      v.n++; votes.set(key, v);
      break;
    }
  }
  let best: { n: number; t: WallTarget } | null = null;
  for (const v of votes.values()) if (!best || v.n > best.n) best = v;
  return best?.t ?? null;
}

export function captureWall(rect: ScreenRect): { crop: string; view: string } {
  return { crop: grab(rect, 1024), view: grab(null, 768) };
}

export async function requestWallDesigns(crop: string, view: string, prompt: string, aspect: number): Promise<{ analysis: string; options: DesignOption[] }> {
  const res = await fetch('/api/design/wall', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await gathrxrAuthHeader()) },
    body: JSON.stringify({ image: crop, view, prompt, aspect }),
  });
  const data: any = await res.json().catch(() => ({}));
  if (!res.ok || !Array.isArray(data.options)) throw new Error(data.error?.message || data.error || data.message || `Design failed (${res.status}).`);
  return data;
}

/** Put a design on the wall: undoable, saved and synced like any other edit. */
export function applyWallDesign(worldId: string | undefined, t: WallTarget, d: DesignOption) {
  const st = useWorldStore.getState();
  const wid = st.actualWorldId || worldId;
  if (t.kind === 'primitive') {
    const prim = st.primitives[t.id];
    if (!prim) return;
    // A mural covers each face once; a pattern repeats about every 1.2 m.
    const [sx, sy] = prim.scale;
    const updates = d.kind === 'mural'
      ? { textureUrl: d.url, texturePreset: undefined, pbrMaps: undefined, textureScale: 1, textureScaleY: 1 }
      : { textureUrl: d.url, texturePreset: undefined, pbrMaps: undefined, textureScale: Math.max(1, Math.round(sx / 1.2)), textureScaleY: Math.max(1, Math.round(sy / 1.2)) };
    st.updatePrimitive(t.id, updates as any);
    if (wid) broadcastObjectUpdate(wid, 'primitive', t.id, updates);
  } else {
    const item = st.contentItems[t.id];
    if (!item) return;
    const materialTextures = { ...(item.materialTextures || {}), [t.slot]: { maps: { colorUrl: d.url }, scale: 1 } };
    st.updateContentItem(t.id, { materialTextures });
    if (wid) broadcastObjectUpdate(wid, 'content', t.id, { materialTextures });
  }
}
